import MagFiltersView from '@utmb/types/view/MagFiltersView';
import SearchMagView from '@utmb/types/view/SearchMagView';
import { StoredFilters } from '@utmb/types/mags';

export interface Action {
    type: string;
}

export interface State {
    isLoaded: boolean;
    isMoreLoaded: boolean;
    mags: SearchMagView[];
    filters: MagFiltersView;
    search: string;
    selectedType: string | null;
    selectedThemes: string[];
    nbHits: number;
    offset: number;
    limit: number;
}

export enum ActionKind {
    LOAD_MODE_MAGS = 'LOAD_MODE_MAGS',
    ADD_MORE_MAGS = 'ADD_MORE_MAGS',
    SET_SEARCH = 'SET_SEARCH',
    FETCH_MAGS = 'FETCH_MAGS',
    SET_MAGS = 'SET_MAGS',
    FILTER_ON_THEME = 'FILTER_ON_THEME',
    FILTER_ON_TYPE = 'FILTER_ON_TYPE',
    RESTORE_FILTERS = 'RESTORE_FILTERS',
}

export interface AddMoreMagsAction extends Action {
    payload: {
        mags: SearchMagView[];
        offset: number;
    };
}

export type LoadMoreMagsAction = Action;

export interface SetSearchAction extends Action {
    payload: string;
}

export type FetchMagsAction = Action;

export interface SetMagsAction extends Action {
    payload: {
        nbHits: number;
        mags: SearchMagView[];
    };
}

export interface FilterOnThemeAction extends Action {
    payload: string;
}

export interface FilterOnTypeAction extends Action {
    payload: string;
}

export interface RestoreFiltersAction extends Action {
    payload: StoredFilters;
}

export const initialState: State = {
    isLoaded: true,
    isMoreLoaded: true,
    mags: [],
    search: '',
    filters: {
        themes: [],
        types: [],
    },
    selectedType: null,
    selectedThemes: [],
    limit: 0,
    nbHits: 0,
    offset: 0,
};

function isLoadMoreMagsAction(action: Action): action is LoadMoreMagsAction {
    return action.type === ActionKind.LOAD_MODE_MAGS;
}

function isAddMoreMagsAction(action: Action): action is AddMoreMagsAction {
    return action.type === ActionKind.ADD_MORE_MAGS;
}

function isSetSearchAction(action: Action): action is SetSearchAction {
    return action.type === ActionKind.SET_SEARCH;
}

function isFetchMagsAction(action: Action): action is FetchMagsAction {
    return action.type === ActionKind.FETCH_MAGS;
}

function isSetMagsAction(action: Action): action is SetMagsAction {
    return action.type === ActionKind.SET_MAGS;
}

function isFilterOnThemeAction(action: Action): action is FilterOnThemeAction {
    return action.type === ActionKind.FILTER_ON_THEME;
}

function isFilterOnTypeAction(action: Action): action is FilterOnTypeAction {
    return action.type === ActionKind.FILTER_ON_TYPE;
}

function isRestoreFilter(action: Action): action is RestoreFiltersAction {
    return action.type === ActionKind.RESTORE_FILTERS;
}

export function reducer(state: State, action: Action) {
    const update: Partial<State> = {};
    if (isLoadMoreMagsAction(action)) {
        update.isMoreLoaded = false;
    }
    if (isAddMoreMagsAction(action)) {
        const { mags, offset } = action.payload;
        update.mags = state.mags.concat(mags);
        update.offset = offset;
        update.isMoreLoaded = true;
    }
    if (isSetSearchAction(action)) {
        update.search = action.payload;
    }
    if (isFetchMagsAction(action)) {
        update.isLoaded = false;
    }
    if (isSetMagsAction(action)) {
        const { mags, nbHits } = action.payload;
        update.mags = mags;
        update.nbHits = nbHits;
        update.offset = 0;
        update.isLoaded = true;
    }
    if (isFilterOnTypeAction(action)) {
        update.selectedType = state.selectedType === action.payload ? null : action.payload;
    }
    if (isFilterOnThemeAction(action)) {
        const theme = action.payload;
        update.selectedThemes = state.selectedThemes.includes(theme) ? state.selectedThemes.filter((t) => t !== theme) : [...state.selectedThemes, theme];
    }
    if (isRestoreFilter(action)) {
        const { search, selectedType, selectedThemes } = action.payload;
        update.search = search;
        update.selectedType = selectedType;
        update.selectedThemes = selectedThemes;
    }

    return {
        ...state,
        ...update,
    };
}
